import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Dialog from "@mui/material/Dialog";
import useMediaQuery from "@mui/material/useMediaQuery";

import { showPurchaseSuccess, showPurchasedTicket } from "../../stores/slices/ticketSlice";
import { getMyTickets } from "../../stores/actions/ticketActions";
import { scheduleDocumentScrollLockRelease } from "../../utils/scrollLock";

const SUCCESS_GREEN = "#3cb44a";
const BRAND_YELLOW = "#ffea00";

/* =========================================================================
 *  Post-purchase success dialog.
 *
 *  Shown once a purchase completes, over the My Tickets page the buyer has
 *  just been routed to. It answers the one question a buyer has the moment
 *  checkout closes — "did that go through?" — and hands off to the QR
 *  viewer.
 *
 *  Deliberately NOT the ticket viewer (PurchasedTicketModal): that one is
 *  the QR carousel a buyer opens to get scanned at the door. This dialog
 *  does not open it — dismissing lands the buyer on their refreshed My
 *  Tickets list, where tapping a ticket opens the viewer as it always has.
 *
 *  Reads the same `purchased_tickets` rows the viewer does, so it needs no
 *  state of its own beyond the open flag.
 * ========================================================================= */
export default function PurchaseSuccessModal() {
    const dispatch = useDispatch();
    const open = useSelector((s) => s.tickets.show_purchase_success);
    const tickets = useSelector((s) => s.tickets.purchased_tickets) || [];
    // The bearer token is the real auth signal — `user` is set for guests too,
    // and /my-tickets is auth-only, so a guest refetch would just 401.
    const token = useSelector((s) => s.auth.token);
    const user = useSelector((s) => s.auth.user);

    // Drives the success mark. Flipped by the Dialog's onEntered rather than
    // set on mount: a CSS animation starts the instant its element mounts,
    // which here is when the Dialog STARTS fading in — so most of the mark
    // played behind a half-transparent dialog and the ring looked like it
    // simply appeared already drawn. Waiting for the dialog to finish opening
    // means the whole thing plays in full view.
    const [entered, setEntered] = useState(false);
    const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

    // Set when this dialog is closing in order to open the ticket viewer, so
    // onExited can skip the scroll-lock release. That release re-runs for up to
    // 800ms after exit; the viewer opens inside that window, so running it
    // anyway would strip the lock the VIEWER had just applied and let the page
    // scroll underneath it. The viewer schedules its own release when it closes.
    const handingOffToViewerRef = useRef(false);

    // Transitions rather than keyframes, so the end state is just "entered ===
    // true". Reduced motion drops the durations and lands on that same state
    // instantly — nothing is left half-drawn or invisible.
    const ease = (spec) => (reduceMotion ? "none" : spec);

    // Dismiss, and pull My Tickets fresh on the way out.
    //
    // The buyer is already standing on /my-tickets — that page fetched its
    // lists when it mounted, which for a just-completed purchase can be
    // before the order rows existed. Refetching here means the list behind
    // this dialog shows the ticket they just bought instead of looking
    // unchanged. `user_id` mirrors the page's own call; the backend
    // authorizes off the bearer token and ignores it.
    const close = () => {
        dispatch(showPurchaseSuccess(false));

        // "View My Ticket(s)" has to actually do that on a phone.
        //
        // Mobile never navigates to /my-tickets — this dialog opens over the
        // event page — so simply dismissing would drop the buyer back on the
        // event they just bought, with the button's promise unkept and their QR
        // nowhere in sight. Hand off to the viewer instead, which is where
        // mobile went directly before this dialog existed.
        //
        // Read matchMedia live rather than through the hook: the same reasoning
        // as the caller in Payment.jsx — a stale render value here silently
        // downgrades mobile to the desktop branch.
        const isMobileNow =
            typeof window !== "undefined" &&
            typeof window.matchMedia === "function" &&
            window.matchMedia("(max-width:767px)").matches;

        if (isMobileNow && tickets.length > 0) {
            handingOffToViewerRef.current = true;
            dispatch(showPurchasedTicket(true));
            return;
        }

        // Desktop: the buyer is standing on /my-tickets, so refresh the list
        // behind the dialog to include what they just bought.
        if (token) {
            dispatch(getMyTickets({ type: "upcoming", user_id: user?.id }));
        }
    };

    // Nothing to confirm without rows — the caller falls back to landing the
    // buyer on My Tickets, which is honest about what it knows.
    if (!tickets.length) return null;

    const qty = tickets.length;

    return (
        <Dialog
            open={!!open}
            onClose={close}
            maxWidth="xs"
            fullWidth
            TransitionProps={{
                onEntered: () => setEntered(true),
                onExited: () => {
                    // Rearm so a second purchase in the same session animates
                    // again instead of opening on the finished state.
                    setEntered(false);
                    if (handingOffToViewerRef.current) {
                        handingOffToViewerRef.current = false;
                        return;
                    }
                    scheduleDocumentScrollLockRelease();
                },
            }}
            PaperProps={{
                sx: {
                    borderRadius: "1.75rem",
                    overflow: "hidden",
                    margin: 2,
                },
            }}
        >
            <div className="bg-white text-center px-7 pt-10 pb-8">
                {/* Success mark: a soft green halo blooms outward while the
                    ring and tick draw themselves. Outlined rather than a solid
                    disc so it stays light against the white card, and green
                    rather than the brand yellow because this mark means "it
                    worked" — yellow is the ticket's colour, and it reads as a
                    highlight, not a confirmation. */}
                <div className="relative mx-auto w-24 h-24 mb-6 flex items-center justify-center">
                    {/* Halo. Blurred so it reads as a glow behind the ring
                        rather than a second, harder circle. */}
                    <span
                        aria-hidden
                        className="absolute inset-0 rounded-full"
                        style={{
                            backgroundColor: SUCCESS_GREEN,
                            filter: "blur(14px)",
                            transform: entered ? "scale(1)" : "scale(0.5)",
                            opacity: entered ? 0.22 : 0,
                            transition: ease(
                                "transform 700ms ease-out, opacity 700ms ease-out"
                            ),
                        }}
                    />
                    <svg
                        aria-hidden
                        viewBox="0 0 24 24"
                        className="relative w-[4.5rem] h-[4.5rem]"
                        fill="none"
                        stroke={SUCCESS_GREEN}
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        {/* Each dasharray exceeds its path length, so offsetting
                            by it hides the stroke until the transition pulls the
                            offset back to 0 — which draws it on. The ring sweeps
                            first, then the tick lands. */}
                        <path
                            d="M21.801 10A10 10 0 1 1 17 3.335"
                            style={{
                                strokeDasharray: 70,
                                strokeDashoffset: entered ? 0 : 70,
                                transition: ease("stroke-dashoffset 600ms ease-out"),
                            }}
                        />
                        <path
                            d="m9 11 3 3L22 4"
                            style={{
                                strokeDasharray: 24,
                                strokeDashoffset: entered ? 0 : 24,
                                transition: ease(
                                    "stroke-dashoffset 350ms ease-out 400ms"
                                ),
                            }}
                        />
                    </svg>
                </div>

                <h2 className="text-3xl font-black text-gray-900 mb-3 leading-tight">
                    You're going!
                </h2>

                {/* The confirmation email is queued by the backend on every
                    successful order, so this is a promise we actually keep. */}
                <p className="text-gray-600 text-base mb-8 leading-relaxed">
                    Your order is complete. We've sent your tickets and receipt
                    to your email address.
                </p>

                <button
                    type="button"
                    onClick={close}
                    className="w-full border-none text-gray-900 font-black py-4 rounded-2xl shadow-md transition-all text-base hover:brightness-95"
                    style={{ backgroundColor: BRAND_YELLOW }}
                >
                    View My Ticket{qty === 1 ? "" : "s"}
                </button>
            </div>
        </Dialog>
    );
}
